import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllPlans, getPlan, getPlanPriceDisplay } from '../config/plansConfig';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import UpgradeModal from '../components/UpgradeModal';
import { trackEvent } from '../lib/analytics';
import './PricingPage.css';

const CHECKOUT_POLL_INTERVAL_MS = 3000;
const CHECKOUT_POLL_MAX_MS = 30000;

function isPaidPlanId(planId) {
  const normalized = String(planId || 'free').trim().toLowerCase();
  return Boolean(normalized && normalized !== 'free');
}

function resolveCheckoutReturnInitialMessage(status, message, t) {
  const normalizedStatus = String(status || '').trim().toLowerCase();
  const normalizedMessage = String(message || '').trim().toUpperCase();

  if (normalizedStatus === 'paid' || normalizedMessage === 'APPROVED') {
    return { phase: 'initial', text: t('pricing.checkoutReturn.confirming') };
  }
  if (normalizedStatus === 'failed') {
    return { phase: 'failed', text: t('pricing.checkoutReturn.failed') };
  }
  return { phase: 'initial', text: t('pricing.checkoutReturn.checking') };
}

const PricingPage = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentPlanMeta, setCurrentPlanMeta] = useState(null);
  const [checkoutBanner, setCheckoutBanner] = useState(null);
  const checkoutReturnHandledRef = useRef(false);

  const fetchCurrentSubscription = useCallback(async () => {
    const response = await fetch(buildApiUrl(API_ENDPOINTS.SUBSCRIPTION_CURRENT), {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    if (data.planId) {
      setCurrentPlan(data.planId);
    }
    if (data.plan) {
      setCurrentPlanMeta(data.plan);
    }
    return data;
  }, []);

  // Load current plan from API
  useEffect(() => {
    if (user?.id) {
      fetchCurrentSubscription().catch((err) => console.error('Error fetching current plan:', err));
    }
  }, [user, fetchCurrentSubscription]);

  // Post-payment return from Moyasar callback (read-only status + poll; no activation).
  useEffect(() => {
    if (searchParams.get('checkout') !== 'return') {
      return undefined;
    }
    if (checkoutReturnHandledRef.current) {
      return undefined;
    }
    checkoutReturnHandledRef.current = true;

    const status = searchParams.get('status');
    const message = searchParams.get('message');
    const shouldPoll = String(status || '').trim().toLowerCase() !== 'failed';

    setCheckoutBanner(resolveCheckoutReturnInitialMessage(status, message, t));
    navigate('/pricing', { replace: true });

    if (!shouldPoll || !user?.id) {
      return undefined;
    }

    let stopped = false;
    let timeoutId = null;
    const startedAt = Date.now();

    const stopPolling = () => {
      if (stopped) return;
      stopped = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleNextPoll = () => {
      if (stopped) return;
      timeoutId = setTimeout(() => {
        void runPoll();
      }, CHECKOUT_POLL_INTERVAL_MS);
    };

    const runPoll = async () => {
      if (stopped) return;

      try {
        const data = await fetchCurrentSubscription();
        const planId = data?.planId;
        if (isPaidPlanId(planId)) {
          setCheckoutBanner({
            phase: 'active',
            text: t('pricing.checkoutReturn.active'),
          });
          stopPolling();
          return;
        }
      } catch (err) {
        console.error('Error polling subscription after checkout return:', err);
      }

      if (Date.now() - startedAt >= CHECKOUT_POLL_MAX_MS) {
        setCheckoutBanner({
          phase: 'pending',
          text: t('pricing.checkoutReturn.pending'),
        });
        stopPolling();
        return;
      }

      scheduleNextPoll();
    };

    void runPoll();

    return () => {
      stopPolling();
    };
  }, [searchParams, user, navigate, t, fetchCurrentSubscription]);

  // Compatibility source of truth for individual plans remains plansConfig.
  const plansConfig = getAllPlans().filter((plan) => plan.id !== 'enterprise' && plan.id !== 'office');
  const individualPlans = plansConfig.map(plan => {
    // Use translations if available, otherwise use config defaults
    const name = t(`pricing.${plan.id}.name`) || (isRTL ? plan.nameAr : plan.name);
    const price = getPlanPriceDisplay(plan.id, billingInterval, isRTL);
    const description = t(`pricing.${plan.id}.description`) || (isRTL ? plan.descriptionAr : plan.description);
    const features = plan.features.map((_, idx) =>
      t(`pricing.${plan.id}.feature${idx + 1}`) || (isRTL ? plan.featuresAr[idx] : plan.features[idx])
    );

    return {
      id: plan.id,
      name,
      price,
      description,
      features,
      buttonText: plan.id === 'free'
        ? t('pricing.free.button', { defaultValue: isRTL ? 'ابدأ الآن' : 'Get Started' })
        : plan.id === 'enterprise'
        ? t('pricing.enterprise.button', { defaultValue: isRTL ? 'اتصل بنا' : 'Contact Us' })
        : t('pricing.pro.button', { defaultValue: isRTL ? 'الترقية الآن' : 'Upgrade Now' }),
      highlighted: plan.highlighted,
      isCurrentPlan: currentPlan === plan.id,
      ctaType: plan.id === 'free' ? 'start_free' : plan.id === 'pro' ? 'upgrade' : 'contact',
      audience: 'individual',
    };
  });

  // Add elite card in UI while keeping backend compatibility (no forced plan migration).
  const individualTierCards = [
    ...individualPlans.filter((p) => p.id !== 'enterprise'),
    {
      id: 'elite',
      name: isRTL ? 'النخبة' : 'Elite',
      price: isRTL ? 'مخصص' : 'Custom',
      description: isRTL ? 'للخبراء والممارسين المتقدمين' : 'For advanced individual practitioners',
      features: isRTL
        ? ['كل مزايا احترافي', 'أولوية أعلى للدعم', 'مزايا متقدمة إضافية', 'إعداد مخصص عند الطلب']
        : ['Everything in Pro', 'Higher-priority support', 'Advanced feature bundle', 'Custom onboarding'],
      buttonText: isRTL ? 'تواصل للمزايا المتقدمة' : 'Contact Sales',
      highlighted: false,
      isCurrentPlan: false,
      ctaType: 'contact',
      audience: 'individual',
    },
  ];

  const officePlanConfig = getPlan('office');
  const firmPlans = [
    {
      id: 'small-firm',
      name: isRTL ? 'مكتب صغير' : 'Small Firm',
      price: isRTL ? 'حسب عدد المقاعد' : 'Per-seat pricing',
      description: isRTL ? 'للمكاتب الناشئة والفرق الصغيرة' : 'For boutique firms and small teams',
      features: isRTL
        ? ['إدارة القضايا الأساسية', 'سعة مبدئية للمستخدمين', 'تقارير الاستخدام', 'دعم قياسي']
        : ['Core case management', 'Starter seat capacity', 'Usage visibility', 'Standard support'],
      cta: isRTL ? 'طلب عرض سعر' : 'Request Quote',
      ctaType: 'contact',
    },
    {
      id: 'office',
      name: t('pricing.office.name', { defaultValue: isRTL ? officePlanConfig.nameAr : officePlanConfig.name }),
      price: getPlanPriceDisplay('office', billingInterval, isRTL),
      description: t('pricing.office.description', {
        defaultValue: isRTL ? officePlanConfig.descriptionAr : officePlanConfig.description,
      }),
      features: officePlanConfig.features.map((_, idx) =>
        t(`pricing.office.feature${idx + 1}`, {
          defaultValue: isRTL ? officePlanConfig.featuresAr[idx] : officePlanConfig.features[idx],
        })
      ),
      cta: t('pricing.office.button', { defaultValue: isRTL ? 'الترقية الآن' : 'Upgrade Now' }),
      ctaType: 'upgrade',
      highlighted: true,
      isCurrentPlan: currentPlan === 'office',
    },
    {
      id: 'enterprise-firm',
      name: isRTL ? 'مؤسسات' : 'Enterprise',
      price: isRTL ? 'تواصل معنا' : 'Contact us',
      description: isRTL ? 'للشركات القانونية الكبيرة والبيئات المعقدة' : 'For large legal organizations',
      features: isRTL
        ? ['اتفاقيات مستوى خدمة', 'تكاملات مخصصة', 'مدير نجاح عميل', 'تهيئة مؤسسية']
        : ['SLA-backed support', 'Custom integrations', 'Dedicated success manager', 'Enterprise onboarding'],
      cta: isRTL ? 'احجز اجتماعاً' : 'Book a Call',
      ctaType: 'contact',
    },
  ];

  const handleIndividualPlanClick = (plan) => {
    trackEvent('pricing_click', { page: '/pricing', metadata: { planId: plan.id, audience: plan.audience || 'individual' } });
    if (plan.ctaType === 'contact') {
      navigate('/contact', { state: { inquiry: 'enterprise_plan' } });
      return;
    }

    if (plan.ctaType === 'start_free') {
      if (!user) {
        navigate('/login', { state: { redirect: '/pricing' } });
        return;
      }
      navigate('/home');
      return;
    }

    if (!user) {
      navigate('/login', { state: { redirect: '/pricing', upgradePlan: plan.id } });
      return;
    }

    if (plan.id === 'free' && currentPlan === 'free') {
      return; // Already on free plan
    }

    if (plan.isCurrentPlan) {
      return; // Already on this plan
    }

    setSelectedPlan(plan.id);
  };

  const handleFirmPlanClick = (plan) => {
    trackEvent('pricing_click', {
      page: '/pricing',
      metadata: { planId: plan.id || 'firm', audience: 'firm', billingInterval },
    });

    if (plan.ctaType === 'upgrade') {
      if (!user) {
        navigate('/login', { state: { redirect: '/pricing', upgradePlan: plan.id } });
        return;
      }
      if (plan.isCurrentPlan) {
        return;
      }
      setSelectedPlan(plan.id);
      return;
    }

    navigate('/contact', { state: { inquiry: 'enterprise_plan' } });
  };

  const billingToggleStyle = {
    display: 'inline-flex',
    gap: '0.5rem',
    marginTop: '1.25rem',
    padding: '0.25rem',
    borderRadius: '999px',
    background: '#e2e8f0',
  };

  const billingToggleButtonStyle = (active) => ({
    border: 'none',
    borderRadius: '999px',
    padding: '0.5rem 1.25rem',
    cursor: 'pointer',
    fontWeight: 600,
    background: active ? '#0066cc' : 'transparent',
    color: active ? '#fff' : '#334155',
  });

  const checkoutBannerStyle = {
    marginBottom: '1.5rem',
    padding: '1rem 1.25rem',
    borderRadius: '12px',
    border:
      checkoutBanner?.phase === 'active'
        ? '1px solid #86efac'
        : checkoutBanner?.phase === 'pending'
          ? '1px solid #fcd34d'
          : checkoutBanner?.phase === 'failed'
            ? '1px solid #fca5a5'
            : '1px solid #93c5fd',
    background:
      checkoutBanner?.phase === 'active'
        ? '#f0fdf4'
        : checkoutBanner?.phase === 'pending'
          ? '#fffbeb'
          : checkoutBanner?.phase === 'failed'
            ? '#fef2f2'
            : '#eff6ff',
    color: '#1e293b',
    fontSize: '0.95rem',
    lineHeight: 1.5,
  };

  return (
    <div className={`pricing-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="pricing-container">
        {checkoutBanner && (
          <div style={checkoutBannerStyle} role="status" aria-live="polite">
            <p style={{ margin: 0 }}>{checkoutBanner.text}</p>
          </div>
        )}
        <div className="pricing-header">
          <h1>{t('pricing.title')}</h1>
          <p>{t('pricing.subtitle')}</p>
          <p className="pricing-soft-note">
            {isRTL
              ? 'التدرج الحالي يدعم الاستخدام المجاني مع رسائل ترقية مرنة دون تعطيل افتراضي.'
              : 'Current rollout supports free usage with soft upgrade messaging by default.'}
          </p>
          {currentPlanMeta && (
            <p className="pricing-current-plan-inline">
              {isRTL ? 'خطتك الحالية:' : 'Current plan:'} {currentPlanMeta.display_name || currentPlan || 'free'}
            </p>
          )}
          <div style={billingToggleStyle} role="group" aria-label={t('pricing.billingInterval.label')}>
            <button
              type="button"
              style={billingToggleButtonStyle(billingInterval === 'monthly')}
              onClick={() => setBillingInterval('monthly')}
            >
              {t('pricing.billingInterval.monthly', { defaultValue: isRTL ? 'شهري' : 'Monthly' })}
            </button>
            <button
              type="button"
              style={billingToggleButtonStyle(billingInterval === 'annual')}
              onClick={() => setBillingInterval('annual')}
            >
              {t('pricing.billingInterval.annual', { defaultValue: isRTL ? 'سنوي' : 'Annual' })}
            </button>
          </div>
        </div>

        <section className="pricing-section">
          <div className="pricing-section-header">
            <h2>{isRTL ? 'باقات الأفراد' : 'Individual Plans'}</h2>
            <p>
              {isRTL
                ? 'للضيوف والمستخدمين الأفراد. ابدأ مجاناً ثم قم بالترقية حسب الحاجة.'
                : 'For guests and individual users. Start free, then upgrade as needed.'}
            </p>
          </div>
          <div className="pricing-grid">
            {individualTierCards.map((plan) => (
              <div
                key={plan.id}
                className={`pricing-card ${plan.highlighted ? 'pricing-card-highlighted' : ''}`}
              >
                {plan.highlighted && (
                  <div className="pricing-badge">{t('pricing.recommended')}</div>
                )}
                <div className="pricing-card-header">
                  <h3 className="pricing-plan-name">{plan.name}</h3>
                  <p className="pricing-plan-description">{plan.description}</p>
                </div>
                <div className="pricing-card-price">
                  <span className="pricing-price">{plan.price}</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="pricing-feature">
                      <span className="pricing-feature-icon">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.isCurrentPlan && (
                  <div className="pricing-current-badge">
                    {isRTL ? 'الخطة الحالية' : 'Current Plan'}
                  </div>
                )}
                <button
                  onClick={() => handleIndividualPlanClick(plan)}
                  className={`pricing-button ${plan.highlighted ? 'pricing-button-highlighted' : ''} ${plan.isCurrentPlan ? 'pricing-button-current' : ''}`}
                  disabled={plan.isCurrentPlan}
                >
                  {plan.buttonText}
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="pricing-section pricing-section-firm">
          <div className="pricing-section-header">
            <h2>{isRTL ? 'باقات المكاتب القانونية' : 'Firm Plans'}</h2>
            <p>
              {isRTL
                ? 'مخصصة للمكاتب والفرق القانونية عبر تكوين اشتراك مؤسسي مناسب.'
                : 'Tailored for office-based legal teams with custom subscription setup.'}
            </p>
          </div>
          <div className="pricing-grid">
            {firmPlans.map((plan) => (
              <div key={plan.id} className={`pricing-card ${plan.highlighted ? 'pricing-card-highlighted' : ''}`}>
                {plan.highlighted && <div className="pricing-badge">{t('pricing.recommended')}</div>}
                <div className="pricing-card-header">
                  <h3 className="pricing-plan-name">{plan.name}</h3>
                  <p className="pricing-plan-description">{plan.description}</p>
                </div>
                <div className="pricing-card-price">
                  <span className="pricing-price">{plan.price}</span>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="pricing-feature">
                      <span className="pricing-feature-icon">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.isCurrentPlan && (
                  <div className="pricing-current-badge">
                    {isRTL ? 'الخطة الحالية' : 'Current Plan'}
                  </div>
                )}
                <button
                  onClick={() => handleFirmPlanClick(plan)}
                  className={`pricing-button ${plan.highlighted ? 'pricing-button-highlighted' : ''} ${plan.isCurrentPlan ? 'pricing-button-current' : ''}`}
                  disabled={plan.isCurrentPlan}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </section>

        <div className="pricing-compat-note">
          {isRTL
            ? 'ملاحظة توافق: عرض باقات الأفراد يعتمد على plansConfig الحالي مع استمرار توافق الاشتراك الفعلي عبر واجهات الاشتراك الحالية.'
            : 'Compatibility note: individual pricing UI is driven by current plansConfig while subscription actions remain on existing subscription endpoints.'}
        </div>
      </div>
      {selectedPlan && (
        <UpgradeModal
          planId={selectedPlan}
          billingInterval={billingInterval}
          onClose={() => setSelectedPlan(null)}
          onSuccess={(data) => {
            setCurrentPlan(selectedPlan);
            setSelectedPlan(null);
          }}
        />
      )}
    </div>
  );
};

export default PricingPage;

